import { FALLBACK_COLOR_MODE }      from '../../shared/constants/colorMode.js'

// ── Null object — returned before config is available ─────────────────────────

const NULL_CAPABILITIES = {
  activeColorModes:         [],
  defaultColorMode:         FALLBACK_COLOR_MODE,
  developerDisplayName:     'APLUS',
  favoritesSectionEnabled:  false,
  hasColorModes:            false,
  heroPaddingEnabled:       false,
  heroSectionEnabled:       false,
  highlightSoldInRed:       false,
  productSectionEnabled:    false,
  reservedUnitsAccessible:  false,
  reservedUnitsPlanVisible: false,
  showPlanThumb:            false,
  showThreeDThumb:          false,
  soldUnitsAccessible:      false,
  soldUnitsPlanVisible:     false,
  tiersSectionEnabled:      false,
}

// ── Helpers ─────────────────────────────────────────────────────────────────────

/**
 * Transforms raw panelSettings model_color_modes into clean, normalized UI capabilities.
 * Sorts by `order` and normalizes property names to camelCase.
 *
 * @param {object|null|undefined} panelSettings - Configuration object from panel-settings.json
 * @returns {{ activeColorModes: Array<{ id: string }>, defaultColorMode: string }}
 */
function deriveColorModes(panelSettings) {
  const rawModes = panelSettings?.model_color_modes?.modes

  if (!Array.isArray(rawModes)) {
    return {
      activeColorModes: [],
      defaultColorMode: FALLBACK_COLOR_MODE,
    }
  }

  const activeColorModes = rawModes
    .filter((mode) => Boolean(mode?.is_active) && Boolean(mode?.id))
    .sort((a, b) => Number(a?.order ?? 0) - Number(b?.order ?? 0))
    .map((mode) => ({
      id: mode.id,
    }))

  const defaultColorMode = activeColorModes[0]?.id ?? FALLBACK_COLOR_MODE

  return {
    activeColorModes,
    defaultColorMode,
  }
}

// ── Pure derivation ────────────────────────────────────

/**
 * Derives App domain capabilities based on project configuration and panel settings.
 * Pure function — used by `useAppCapabilities` and unit tests.
 *
 * @param {object|null} projectConfig - Configuration object from panel-config.json
 * @param {object|null} panelSettings - Configuration object from panel-settings.json
 * @returns {{
 *   activeColorModes:        object[],
 *   defaultColorMode:        string,
 *   developerDisplayName:    string,
 *   favoritesSectionEnabled: boolean,
 *   hasColorModes:           boolean,
 *   heroPaddingEnabled:      boolean,
 *   heroSectionEnabled:      boolean,
 *   highlightSoldInRed:      boolean,
 *   productSectionEnabled:   boolean,
 *   reservedUnitsAccessible: boolean,
 *   reservedUnitsPlanVisible: boolean,
 *   showPlanThumb:           boolean,
 *   showThreeDThumb:         boolean,
 *   soldUnitsAccessible:     boolean,
 *   soldUnitsPlanVisible:    boolean,
 *   tiersSectionEnabled:     boolean,
 * }} App capabilities object
 */
export function deriveAppCapabilities(projectConfig, panelSettings, panelStyles) {
  if (!projectConfig?.app_config) {
    return NULL_CAPABILITIES;
  }
  const appConfig = projectConfig.app_config ?? {};
  const sections = appConfig.website?.sections ?? {};

  const developerDisplayName = projectConfig.developer_display_name ?? 'APLUS';
  
  const heroSectionEnabled = sections?.hero === true;
  const tiersSectionEnabled = sections?.tiers === true;
  // Default to true: product section is fundamental unless explicitly disabled.
  const productSectionEnabled = sections?.product !== false;
  const favoritesSectionEnabled = sections?.favorites === true;

  const effectiveSettings = {...panelSettings};
  const effectiveStyles = {...panelStyles};

  const heroVisibility = effectiveSettings?.hero_visibility?.render_hero_module
  const heroPaddingEnabled = effectiveSettings?.hero_padding?.add_padding //move to styles

  const modelLoadConfig = effectiveSettings?.model_load_config
  const highlightSoldInRed = modelLoadConfig?.highlight_sold_in_red ?? true
  const showPlanThumb = modelLoadConfig?.show_plan_thumb ?? true
  const showThreeDThumb = modelLoadConfig?.show_three_d_thumb ?? false
  
  const { activeColorModes, defaultColorMode } = deriveColorModes(effectiveSettings)
  const hasColorModes = activeColorModes.length > 0

  const unitSheetAccessibility = effectiveSettings?.unit_sheet_accessibility
  const reservedUnitsAccessible = unitSheetAccessibility?.reserved_units.is_accessible ?? true
  const reservedUnitsPlanVisible = unitSheetAccessibility?.reserved_units.plan_visible ?? true
  const soldUnitsAccessible = unitSheetAccessibility?.sold_units.is_accessible ?? true
  const soldUnitsPlanVisible = unitSheetAccessibility?.sold_units.plan_visible ?? true

  return {
    activeColorModes,
    defaultColorMode,
    developerDisplayName,
    favoritesSectionEnabled,
    hasColorModes,
    heroSectionEnabled,
    heroPaddingEnabled,
    highlightSoldInRed,
    productSectionEnabled,
    reservedUnitsAccessible,
    reservedUnitsPlanVisible,
    showPlanThumb,
    showThreeDThumb,
    soldUnitsAccessible,
    soldUnitsPlanVisible,
    tiersSectionEnabled,
  }
}